import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppId, DAG_APPS, DagApp, appById } from "../apps/registry";
import AppLogo from "./AppLogo";

interface Props {
  currentAppId?: AppId;
}

const AppSwitcher: React.FC<Props> = ({ currentAppId = "orchestration" }) => {
  const current = appById(currentAppId);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (app: DagApp) => {
    if (app.id === currentAppId) {
      setOpen(false);
      return;
    }
    if (app.externalUrl) {
      window.location.assign(app.externalUrl);
      return;
    }
    if (app.homePath) {
      navigate(app.homePath);
      setOpen(false);
      return;
    }
    setOpen(false);
  };

  const unavailable = (app: DagApp) =>
    app.id !== currentAppId && !app.homePath && !app.externalUrl;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full flex flex-nowrap items-center gap-2.5 px-1 py-1 -mx-1 text-left hover:bg-white/4 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C9A260]"
      >
        <AppLogo
          src={current.logoSrc}
          paths={current.iconPaths}
          mark={current.mark}
          accent={current.accent}
          bg={current.logoBg}
          alt={current.name}
        />
        <div className="min-w-0 flex-1">
          <div className="text-[#E5E0D5] text-sm font-semibold leading-tight truncate">
            {current.name}
          </div>
          <div className="text-[10px] text-[#5A5448] uppercase tracking-widest truncate whitespace-nowrap">
            {current.tagline}
          </div>
        </div>
        <svg
          className={`h-3.5 w-3.5 text-[#5A5448] flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Switch app"
          className="absolute left-0 right-0 top-full mt-1 z-50 border border-[#262420] bg-[#1a1917] shadow-lg py-1"
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
            Apps
          </div>
          {DAG_APPS.map((app) => {
            const active = app.id === currentAppId;
            const disabled = unavailable(app);
            return (
              <button
                key={app.id}
                type="button"
                role="option"
                aria-selected={active}
                disabled={disabled}
                onClick={() => select(app)}
                className={`w-full flex flex-nowrap items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  active
                    ? "bg-[#C9A260]/10"
                    : disabled
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-white/4"
                }`}
                title={
                  disabled
                    ? "Set REACT_APP_BRAIN_URL to open Brain"
                    : undefined
                }
              >
                <AppLogo
                  src={app.logoSrc}
                  paths={app.iconPaths}
                  mark={app.mark}
                  accent={app.accent}
                  bg={app.logoBg}
                  size="sm"
                  alt={app.name}
                />
                <div className="min-w-0">
                  <div className={`text-sm font-medium leading-tight whitespace-nowrap ${active ? "text-[#C9A260]" : "text-[#E5E0D5]"}`}>
                    {app.name}
                  </div>
                  <div className="text-[10px] text-[#5A5448] uppercase tracking-widest whitespace-nowrap">
                    {app.tagline}
                  </div>
                </div>
                {active && (
                  <span
                    className="ml-auto h-1.5 w-1.5 rounded-full bg-[#C9A260] flex-shrink-0"
                    aria-label="Current app"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
